/** Server writes; financial statuses refresh only after the server responds. */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { type BankCardSummary, type KycSnapshot } from '@shared/types';
import type { TrafficStatus, WithdrawalRequest } from '@shared/ledger';
import { api } from './api';
import { endpoints } from './endpoints';
import { queryKeys } from './queryKeys';
import type {
  AuthResponse,
  ConfirmResult,
  CreateCardRequest,
  CreateDisputeRequest,
  CreateWithdrawalRequest,
  NotificationPrefs,
  SetCardEnabledRequest,
  TraderCard,
  TraderTransaction,
  TwoFaSetup,
  UpdateCardLimitsRequest,
} from '@/types/api';

// ---------- Auth ----------

export interface Credentials {
  email: string;
  password: string;
}

export interface SignupPayload extends Credentials {
  phone?: string;
}

/** Both auth endpoints may answer flat or wrapped; normalise once, here. */
function normaliseAuth(data: unknown): AuthResponse {
  const record = data as Partial<AuthResponse> & Partial<{ accessToken: string; expiresIn: number }>;
  if (record.tokens && record.trader) {
    return { tokens: record.tokens, trader: record.trader };
  }
  return {
    tokens: {
      accessToken: record.accessToken ?? '',
      expiresIn: record.expiresIn ?? 3600,
    },
    trader: record.trader ?? null,
  };
}

export function useLogin(): UseMutationResult<AuthResponse, unknown, Credentials> {
  return useMutation({
    mutationFn: async (credentials) => {
      const { data } = await api.post<unknown>(endpoints.auth.login, credentials);
      return normaliseAuth(data);
    },
  });
}

export function useSignup(): UseMutationResult<AuthResponse, unknown, SignupPayload> {
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<unknown>(endpoints.auth.signup, payload);
      return normaliseAuth(data);
    },
  });
}

// ---------- Traffic ----------

export function useConfirmTransaction(): UseMutationResult<
  ConfirmResult,
  unknown,
  TraderTransaction
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transaction) => {
      const { data } = await api.post<ConfirmResult>(
        endpoints.transactions.confirm(transaction.id),
      );
      return data;
    },

    // Keep the server's status and countdown until the write completes.
    // Cached lists include both arrays and paginated objects; no speculative
    // financial state is written into either representation.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.transactions.all });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.balance });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stats });
      // A confirmation releases frozen collateral and credits commission, so
      // every figure on Финансы just changed — and freeing collateral can also
      // unblock traffic that was stopped for want of it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.money.all });
    },
  });
}

export function useCreateDispute(): UseMutationResult<unknown, unknown, CreateDisputeRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, reason, statement, evidence }) => {
      const form = new FormData();
      form.append('transactionId', transactionId);
      form.append('reason', reason);
      form.append('statement', statement);
      for (const file of evidence) {
        form.append('evidence', file, file.name);
      }

      const { data } = await api.post<unknown>(endpoints.disputes.create, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000, // Screenshots on a mobile connection.
      });
      return data;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

// ---------- Verification ----------

export function useUploadDocument(
  kind: 'passport' | 'selfie',
): UseMutationResult<unknown, unknown, File> {
  const queryClient = useQueryClient();
  const path = kind === 'passport' ? endpoints.kyc.passport : endpoints.kyc.selfie;

  return useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('document', file, file.name);

      const { data } = await api.post<unknown>(path, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
    },
  });
}

export function useAddCard(): UseMutationResult<BankCardSummary, unknown, CreateCardRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<BankCardSummary>(endpoints.cards.create, payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards });
    },
  });
}

export function useRemoveCard(): UseMutationResult<unknown, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cardId) => {
      const { data } = await api.delete<unknown>(endpoints.cards.remove(cardId));
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards });
    },
  });
}

export function useSubmitKyc(): UseMutationResult<unknown, unknown, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<unknown>(endpoints.kyc.submit);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
    },
  });
}

// ---------- Settings ----------

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export function useChangePassword(): UseMutationResult<unknown, unknown, ChangePasswordPayload> {
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<unknown>(endpoints.account.changePassword, payload);
      return data;
    },
  });
}

export interface UpdateProfilePayload {
  phone: string | null;
}

export function useUpdateProfile(): UseMutationResult<unknown, unknown, UpdateProfilePayload> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.patch<unknown>(endpoints.account.profile, payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useTwoFaSetup(): UseMutationResult<TwoFaSetup, unknown, void> {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TwoFaSetup>(endpoints.account.twoFaSetup);
      return data;
    },
  });
}

export function useTwoFaEnable(): UseMutationResult<unknown, unknown, { code: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<unknown>(endpoints.account.twoFaEnable, payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useTwoFaDisable(): UseMutationResult<unknown, unknown, { password: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<unknown>(endpoints.account.twoFaDisable, payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useRevokeSession(): UseMutationResult<unknown, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId) => {
      const { data } = await api.delete<unknown>(endpoints.account.revokeSession(sessionId));
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useUpdateNotificationPrefs(): UseMutationResult<
  unknown,
  unknown,
  NotificationPrefs
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.put<unknown>(endpoints.account.notificationPrefs, payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationPrefs });
    },
  });
}

// ---------- The money layer ----------

/**
 * Turning the trader's own switch off is unambiguous: nothing is routed to
 * them and the reason is their switch.
 *
 * Turning it on only clears *their* block. The server answers with the first
 * blocking reason in priority order, so a status that currently reads
 * `traffic_disabled` may still have an unverified card or exhausted collateral
 * behind it — which is why the optimistic "on" state is only ever as good as
 * the last answer, and why the PUT's own response is written straight into the
 * cache the moment it lands.
 */
function optimisticTraffic(current: TrafficStatus, isEnabled: boolean): TrafficStatus {
  // A block that outranks the trader's own switch survives either direction.
  // `kyc_not_approved` is ordered above `traffic_disabled` in the shared
  // contract, so a trader who has not passed verification and flips their
  // switch off must keep seeing the verification reason and its call to
  // action. Overwriting it here replaced that with a bare "приём выключен"
  // — and its copy has no destination — until the PUT came back.
  const higherPriorityBlock =
    current.blockedBy && current.blockedBy !== 'traffic_disabled' ? current.blockedBy : null;

  if (!isEnabled) {
    return {
      ...current,
      isEnabled: false,
      canReceive: false,
      blockedBy: higherPriorityBlock ?? 'traffic_disabled',
    };
  }

  return {
    ...current,
    isEnabled: true,
    canReceive: higherPriorityBlock === null,
    blockedBy: higherPriorityBlock,
  };
}

/**
 * The switch a trader reaches for when they go to sleep.
 *
 * Optimistic, because the alternative is a control that does not move for as
 * long as the network takes and gets pressed twice. Rolled back on failure —
 * the caller surfaces the error next to the switch, since a switch that
 * silently sprang back is worse than one that never moved.
 */
export function useSetTraffic(): UseMutationResult<
  TrafficStatus,
  unknown,
  boolean,
  { previous: TrafficStatus | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isEnabled) => {
      const { data } = await api.put<TrafficStatus>(endpoints.money.traffic, { isEnabled });
      return data;
    },

    onMutate: async (isEnabled) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.money.traffic });
      const previous = queryClient.getQueryData<TrafficStatus>(queryKeys.money.traffic);

      if (previous) {
        queryClient.setQueryData<TrafficStatus>(
          queryKeys.money.traffic,
          optimisticTraffic(previous, isEnabled),
        );
      }
      return { previous };
    },

    onError: (_error, _isEnabled, context) => {
      queryClient.setQueryData(queryKeys.money.traffic, context?.previous);
    },

    // The server's answer is the authority on every field, including a block
    // the client could not have known about.
    onSuccess: (status) => {
      queryClient.setQueryData(queryKeys.money.traffic, status);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.money.traffic });
    },
  });
}

/**
 * Requesting a withdrawal.
 *
 * Deliberately not optimistic: nothing about this is reversible from the
 * client's side, and a request that appeared in the history and then vanished
 * would be read as money that had gone somewhere.
 */
export function useCreateWithdrawal(): UseMutationResult<
  WithdrawalRequest,
  unknown,
  CreateWithdrawalRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<WithdrawalRequest>(endpoints.money.withdrawals, payload);
      return data;
    },
    onSuccess: () => {
      // Balances, history and the statement all moved together.
      void queryClient.invalidateQueries({ queryKey: queryKeys.money.all });
    },
  });
}

export function useUpdateCardLimits(): UseMutationResult<
  unknown,
  unknown,
  UpdateCardLimitsRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cardId, limits }) => {
      const { data } = await api.patch<unknown>(endpoints.cards.limits(cardId), limits);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards });
      // Raising a ceiling can be exactly what unblocks routing.
      void queryClient.invalidateQueries({ queryKey: queryKeys.money.traffic });
    },
  });
}

/** The KYC snapshot as the cards screen reads it — cards carry money fields. */
type KycWithCards = Omit<KycSnapshot, 'cards'> & { cards: TraderCard[] };

/**
 * The per-card switch. Optimistic for the same reason as the traffic one: a
 * switch is a physical metaphor and has to move under the thumb.
 */
export function useSetCardEnabled(): UseMutationResult<
  unknown,
  unknown,
  SetCardEnabledRequest,
  { previous: KycWithCards | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cardId, isEnabled }) => {
      const { data } = await api.patch<unknown>(endpoints.cards.enabled(cardId), { isEnabled });
      return data;
    },

    onMutate: async ({ cardId, isEnabled }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.kyc });
      const previous = queryClient.getQueryData<KycWithCards>(queryKeys.kyc);

      queryClient.setQueryData<KycWithCards>(queryKeys.kyc, (current) =>
        current
          ? {
              ...current,
              cards: current.cards.map((card) =>
                card.id === cardId ? { ...card, isEnabled } : card,
              ),
            }
          : current,
      );

      return { previous };
    },

    onError: (_error, _payload, context) => {
      queryClient.setQueryData(queryKeys.kyc, context?.previous);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards });
      void queryClient.invalidateQueries({ queryKey: queryKeys.money.traffic });
    },
  });
}
